/**
 * Organization identification / invoice-disclosure registration numbers.
 *
 * These fields are independent of CrewPilot tax calculation. Presence of a
 * registration number must never enable taxes, change rates, or alter totals.
 */

export const ORGANIZATION_REGISTRATION_MAX_LENGTH = 80;

export const ORGANIZATION_REGISTRATION_LABELS = Object.freeze({
  businessNumber: "Business Number",
  gstHstRegistrationNumber: "GST/HST Registration Number",
  qstRegistrationNumber: "QST Registration Number",
});

export const ORGANIZATION_REGISTRATION_DEFAULTS = Object.freeze({
  businessNumber: "",
  gstHstRegistrationNumber: "",
  qstRegistrationNumber: "",
  additionalRegistrationLabel: "",
  additionalRegistrationNumber: "",
});

function hasDisallowedRegistrationChars(text) {
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 32 || code === 127 || text[i] === "<" || text[i] === ">") {
      return true;
    }
  }
  return false;
}

export function normalizeRegistrationText(value) {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

export function isValidRegistrationText(value) {
  const text = normalizeRegistrationText(value);
  if (!text) return true;
  if (text.length > ORGANIZATION_REGISTRATION_MAX_LENGTH) return false;
  if (hasDisallowedRegistrationChars(text)) return false;
  return true;
}

function readRegistrationField(source, camelKey, snakeKey) {
  if (!source || typeof source !== "object") return "";
  if (source[camelKey] != null && source[camelKey] !== "") {
    return normalizeRegistrationText(source[camelKey]);
  }
  return normalizeRegistrationText(source[snakeKey]);
}

/**
 * Normalize registration fields from settings.organization, a DB row, or a form.
 */
export function resolveOrganizationRegistration(source = {}) {
  const org =
    source.organization && typeof source.organization === "object"
      ? source.organization
      : source;

  return {
    businessNumber: readRegistrationField(
      org,
      "businessNumber",
      "business_number",
    ),
    gstHstRegistrationNumber: readRegistrationField(
      org,
      "gstHstRegistrationNumber",
      "gst_hst_registration_number",
    ),
    qstRegistrationNumber: readRegistrationField(
      org,
      "qstRegistrationNumber",
      "qst_registration_number",
    ),
    additionalRegistrationLabel: readRegistrationField(
      org,
      "additionalRegistrationLabel",
      "additional_registration_label",
    ),
    additionalRegistrationNumber: readRegistrationField(
      org,
      "additionalRegistrationNumber",
      "additional_registration_number",
    ),
  };
}

export function organizationRegistrationToPatch(registration) {
  const resolved = resolveOrganizationRegistration(registration);
  return {
    business_number: resolved.businessNumber,
    gst_hst_registration_number: resolved.gstHstRegistrationNumber,
    qst_registration_number: resolved.qstRegistrationNumber,
    additional_registration_label: resolved.additionalRegistrationLabel,
    additional_registration_number: resolved.additionalRegistrationNumber,
  };
}

/**
 * Invoice display lines. Empty values are omitted — never render a bare label.
 */
export function buildOrganizationRegistrationLines(source) {
  const registration = resolveOrganizationRegistration(source);
  const lines = [];

  if (registration.businessNumber) {
    lines.push(
      `${ORGANIZATION_REGISTRATION_LABELS.businessNumber}: ${registration.businessNumber}`,
    );
  }
  if (registration.gstHstRegistrationNumber) {
    lines.push(
      `${ORGANIZATION_REGISTRATION_LABELS.gstHstRegistrationNumber}: ${registration.gstHstRegistrationNumber}`,
    );
  }
  if (registration.qstRegistrationNumber) {
    lines.push(
      `${ORGANIZATION_REGISTRATION_LABELS.qstRegistrationNumber}: ${registration.qstRegistrationNumber}`,
    );
  }
  if (
    registration.additionalRegistrationLabel &&
    registration.additionalRegistrationNumber
  ) {
    lines.push(
      `${registration.additionalRegistrationLabel}: ${registration.additionalRegistrationNumber}`,
    );
  }

  return lines;
}

export function validateOrganizationRegistration(source) {
  const registration = resolveOrganizationRegistration(source);
  const errors = {};

  const fieldChecks = [
    ["businessNumber", ORGANIZATION_REGISTRATION_LABELS.businessNumber],
    [
      "gstHstRegistrationNumber",
      ORGANIZATION_REGISTRATION_LABELS.gstHstRegistrationNumber,
    ],
    ["qstRegistrationNumber", ORGANIZATION_REGISTRATION_LABELS.qstRegistrationNumber],
    ["additionalRegistrationLabel", "Additional registration label"],
    ["additionalRegistrationNumber", "Additional registration number"],
  ];

  for (const [key, label] of fieldChecks) {
    if (!isValidRegistrationText(registration[key])) {
      errors[key] =
        `${label} must be ${ORGANIZATION_REGISTRATION_MAX_LENGTH} characters or fewer and cannot contain < or >.`;
    }
  }

  const hasLabel = Boolean(registration.additionalRegistrationLabel);
  const hasNumber = Boolean(registration.additionalRegistrationNumber);
  if (hasLabel && !hasNumber) {
    errors.additionalRegistrationNumber =
      "Enter a number for the additional registration, or clear the label.";
  }
  if (hasNumber && !hasLabel) {
    errors.additionalRegistrationLabel =
      "Enter a label for the additional registration, or clear the number.";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    registration,
  };
}
